import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";

// App setting values are stored as JSON blobs; callers pass `unknown` and we
// narrow to Zero's JSON payload type at the mutator boundary.
type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

type ZeroAppSetting = {
  key: string;
  value: unknown;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useAppSetting<T = unknown>(key: string) {
  const [data, details] = useQuery(appQueries.appSettings.byKey({ key }));
  const setting = data as ZeroAppSetting | undefined | null;

  return {
    data: (setting?.value ?? null) as T | null,
    isLoading: details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useUpdateAppSetting() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ key, value }: { key: string; value: unknown }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.appSettings.update({ key, value: value as JsonValue }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Setting update was rejected",
      );
      return { key, value };
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { key: string; value: unknown },
      options?: MutationOptions<{ key: string; value: unknown }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Setting saved");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to save setting: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
